import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import * as Sentry from "@sentry/angular";
import { EMPTY, Observable, of, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { LoginModalComponent } from "../app/users/pages/login-modal/login-modal.component";
import { AuthService } from "../app/users/services/auth.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  modalShow: boolean = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private modalService: NgbModal
  ) {}

  private async extractErrorBody(error: any): Promise<any> {
    if (error instanceof Blob) {
      try {
        const text = await error.text();
        return JSON.parse(text);
      } catch {
        return { type: "Blob", size: error.size, mimeType: error.type };
      }
    }
    return error;
  }

  private serializeRequest(req: HttpRequest<any>): any {
    return {
      method: req.method,
      url: req.url,
      urlWithParams: req.urlWithParams,
      headers: req.headers.keys().reduce(
        (acc, key) => {
          if (key.toLowerCase() !== "authorization") {
            acc[key] = req.headers.get(key);
          }
          return acc;
        },
        {} as Record<string, string | null>
      ),
      params: req.params,
      context: req.context,
      body: JSON.parse(JSON.stringify(req.serializeBody())),
    };
  }

  private handleAuthError(
    err: HttpErrorResponse,
    req: HttpRequest<any>
  ): Observable<HttpEvent<any>> {
    if (!(err instanceof HttpErrorResponse)) {
      return throwError(() => err);
    }

    if (localStorage.getItem("disableSigninModal") === "1") {
      return of(err.message) as unknown as Observable<HttpEvent<any>>;
    }

    if (err.status === 0) {
      return EMPTY;
    }
    //handle your auth error or rethrow
    if (err.status === 401 || err.status === 403) {
      //navigate /delete cookies or whatever
      //  window.location.href = this.authService.getLoginUrl(window.location.href)
      console.log("401");
      localStorage.removeItem("accessKey");
      Sentry.setUser(null);
      if (req.url.indexOf("user/signin") > -1) {
        localStorage.removeItem("thrustKey");
        this.signIn();
      }
      if (!this.modalShow) this.signIn();
      return of(err.message) as unknown as Observable<HttpEvent<any>>;
    }

    // All other errors: re-throw so components can handle them.
    // Unhandled errors will be caught by GlobalErrorHandler.
    return throwError(() => err);
  }

  private enrichSentryContext(err: HttpErrorResponse, req: HttpRequest<any>): void {
    const urlWithoutIds = err.url?.replace(/\d+/g, "{id}") || "unknown-url";

    (err as any).__sentryContext = {
      fingerprint: ["{{ default }}", err.status.toString(), urlWithoutIds],
      backendDetails: {
        status: err.status,
        statusText: err.statusText,
        url: err.url,
        method: req.method,
        errorMessage: err.message,
        errorDetails: err.error,
      },
      requestDetails: this.serializeRequest(req),
    };

    if (err.error instanceof Blob) {
      this.extractErrorBody(err.error).then((errorBody) => {
        (err as any).__sentryContext.backendDetails.errorDetails = errorBody;
      });
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Clone the request to add the new header.
    var authReq = req.clone();
    console.log(req.url);
    if (
      localStorage.getItem("accessKey") &&
      (req.url.indexOf("assist") > -1 || req.url.indexOf("localhost") > -1)
    ) {
      authReq = req.clone({
        headers: req.headers.set("Authorization", "Bearer " + localStorage.getItem("accessKey")),
      });
    } else {
      if (
        (req.url.indexOf("assist") > -1 || req.url.indexOf("localhost") > -1) &&
        (req.url.indexOf("signin") > -1 || req.url.indexOf("signedin") > -1)
      )
        authReq = req.clone({
          withCredentials: true,
          headers: req.headers.set("Authorization", this.authService.getAppKey()),
        });
      else authReq = req.clone();
    }
    // catch the error, make specific functions for catching specific errors and you can chain through them with more catch operators
    return next.handle(authReq).pipe(
      tap({
        error: (err: any) => {
          if (
            err instanceof HttpErrorResponse &&
            err.status !== 401 &&
            err.status !== 403 &&
            err.status !== 0
          ) {
            this.enrichSentryContext(err, req);
          }
        },
      }),
      catchError((err) => this.handleAuthError(err, req))
    );
  }

  signIn() {
    this.modalShow = true;
    var modal = this.modalService.open(LoginModalComponent, {
      windowClass: "fs-modal",
    });
    this.router.navigateByUrl("/", { skipLocationChange: true });
    modal.dismissed.subscribe((x) => {
      this.closedSignIn();
    });
    modal.closed.subscribe((x) => {
      this.closedSignIn();
    });
  }

  closedSignIn() {
    if (!localStorage.getItem("accessKey") && localStorage.getItem("disableSigninModal") !== "1") {
      this.signIn();
    } else {
      this.modalShow = false;
    }
  }
}
